import { brandPrimary } from '../../../../styles/colors';
import { styleSheet } from '../styles';
import { CalendarDateIcon } from '@WebComponents/svgs/icons/CalendarDateIcon';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';

// Deal stages that should NOT show the recording link (lowercase for case-insensitive comparison)
const EXCLUDED_RECORDING_STAGES = ['appointment no show', 'appointment rescheduled', 'appointment scheduled'] as const;

const PAST_DEMO_THRESHOLD_HOURS = 1;

interface IDemoDateWithRecordingProps {
	dateValue: string;
	demoId?: string; // Actually companyId, but keeping same name for simplicity
	closerOwnerId?: string;
	dealStage?: string; // To check for no-show status
	companyName?: string;
	onRecordingClick?: (demoId: string, demoDate: string, closerOwnerId: string, companyName?: string) => void;
}

export const DemoDateWithRecording: React.FC<IDemoDateWithRecordingProps> = ({
	dateValue,
	demoId,
	closerOwnerId,
	dealStage,
	companyName,
	onRecordingClick,
}) => {
	if (!dateValue) {
		return <div>no date to show</div>;
	}

	// Check if the UTC time is midnight (indicates date-only value)
	const utcMoment = moment.utc(dateValue);
	const isUtcMidnight = utcMoment.hours() === 0 && utcMoment.minutes() === 0 && utcMoment.seconds() === 0;

	// For midnight UTC times, don't convert timezone - treat as date-only
	// For other times, convert to local browser time
	const momentDate = isUtcMidnight ? utcMoment : moment(dateValue);
	const formattedDate = momentDate.format(`M/DD/YY${isUtcMidnight ? '' : ' h:mm A'}`);

	// Check if demo is in the past (more than threshold hours ago)
	// Note: For now using UTC, can enhance with timezone later
	const now = moment();
	const demoTime = moment(dateValue);
	const isPastDemo = now.diff(demoTime, 'hours') >= PAST_DEMO_THRESHOLD_HOURS;

	// Check if deal stage should exclude the recording link
	const isExcludedStage =
		dealStage && EXCLUDED_RECORDING_STAGES.some(excluded => dealStage.toLowerCase() === excluded.toLowerCase());

	// Only show recording link for past demos with non-excluded stages
	const shouldShowRecording = isPastDemo && demoId && closerOwnerId && !isExcludedStage;

	const handleRecordingClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		if (onRecordingClick && demoId && closerOwnerId) {
			onRecordingClick(demoId, dateValue, closerOwnerId, companyName);
		}
	};

	return (
		<div className={css(styleSheet.lastDealContainer)}>
			<div>
				<span>{formattedDate}</span>
				<CalendarDateIcon fillColor={brandPrimary} className={css(styleSheet.iconPosition)} />
			</div>
			{shouldShowRecording && (
				<div style={{ marginTop: '4px' }}>
					<button
						type='button'
						onClick={handleRecordingClick}
						className={css(styleSheet.recordingLink)}
						aria-label={`View demo recordings from ${formattedDate}`}
					>
						Recording
					</button>
				</div>
			)}
		</div>
	);
};
